import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useWallet } from './WalletContext'

// Sui Stack Messaging types (based on the SDK documentation)
interface Message {
  id: string
  sender: string
  recipient: string
  content: string
  timestamp: number
  attachments?: MessageAttachment[]
  encrypted: boolean
}

interface MessageAttachment {
  id: string
  walrusId: string
  filename: string
  mimeType: string
  size: number
}

interface Conversation {
  id: string
  participants: string[]
  lastMessage?: Message
  unreadCount: number
  createdAt: number
}

interface MessagingContextType {
  conversations: Conversation[]
  currentConversation: Conversation | null
  messages: Message[]
  loading: boolean
  error: string | null
  
  // Actions
  createConversation: (participants: string[]) => Promise<string>
  sendMessage: (conversationId: string, content: string, attachments?: File[]) => Promise<void>
  loadMessages: (conversationId: string) => Promise<void>
  setCurrentConversation: (conversation: Conversation | null) => void
  markAsRead: (conversationId: string) => Promise<void>
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined)

interface MessagingProviderProps {
  children: ReactNode
}

export function MessagingProvider({ children }: MessagingProviderProps) {
  const { address, client } = useWallet()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load conversations on wallet connection
  useEffect(() => {
    if (address && client) {
      loadConversations()
    }
  }, [address, client])

  const loadConversations = async () => {
    if (!address || !client) return

    setLoading(true)
    try {
      // In production, this would query the Sui Stack Messaging contracts
      // For now, we'll use mock data
      const mockConversations: Conversation[] = [
        {
          id: 'conv-1',
          participants: [address, '0x' + Math.random().toString(16).substr(2, 40)],
          lastMessage: {
            id: 'msg-1',
            sender: '0x' + Math.random().toString(16).substr(2, 40),
            recipient: address,
            content: 'Hey! Love your latest content on HDH!',
            timestamp: Date.now() - 3600000,
            encrypted: true
          },
          unreadCount: 2,
          createdAt: Date.now() - 86400000
        },
        {
          id: 'conv-2',
          participants: [address, '0x' + Math.random().toString(16).substr(2, 40)],
          lastMessage: {
            id: 'msg-2',
            sender: address,
            recipient: '0x' + Math.random().toString(16).substr(2, 40),
            content: 'Thanks for subscribing! Here\'s some exclusive content.',
            timestamp: Date.now() - 7200000,
            encrypted: true
          },
          unreadCount: 0,
          createdAt: Date.now() - 172800000
        }
      ]

      setConversations(mockConversations)
    } catch (err) {
      setError('Failed to load conversations')
      console.error('Error loading conversations:', err)
    } finally {
      setLoading(false)
    }
  }

  const createConversation = async (participants: string[]): Promise<string> => {
    if (!address || !client) throw new Error('Wallet not connected')

    setLoading(true)
    try {
      // In production, this would create a conversation on-chain
      const conversationId = 'conv-' + Date.now()
      
      const newConversation: Conversation = {
        id: conversationId,
        participants,
        unreadCount: 0,
        createdAt: Date.now()
      }

      setConversations(prev => [newConversation, ...prev])
      return conversationId
    } catch (err) {
      setError('Failed to create conversation')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async (
    conversationId: string, 
    content: string, 
    attachments?: File[]
  ): Promise<void> => {
    if (!address || !client) throw new Error('Wallet not connected')

    setLoading(true)
    try {
      // Handle attachments with Walrus
      const messageAttachments: MessageAttachment[] = []
      if (attachments) {
        for (const file of attachments) {
          // In production, upload to Walrus
          const attachment: MessageAttachment = {
            id: 'att-' + Date.now(),
            walrusId: 'walrus-' + Math.random().toString(16).substr(2, 8),
            filename: file.name,
            mimeType: file.type,
            size: file.size
          }
          messageAttachments.push(attachment)
        }
      }

      const newMessage: Message = {
        id: 'msg-' + Date.now(),
        sender: address,
        recipient: currentConversation?.participants.find(p => p !== address) || '',
        content,
        timestamp: Date.now(),
        attachments: messageAttachments,
        encrypted: true
      }

      // Add message to current conversation
      setMessages(prev => [...prev, newMessage])

      // Update conversation's last message
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, lastMessage: newMessage }
          : conv
      ))

      // In production, this would send the message on-chain with Seal encryption
      console.log('Sending encrypted message:', newMessage)
    } catch (err) {
      setError('Failed to send message')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (_conversationId: string): Promise<void> => {
    if (!address || !client) return

    setLoading(true)
    try {
      // In production, this would load messages from the blockchain
      // For now, we'll use mock data
      const mockMessages: Message[] = [
        {
          id: 'msg-1',
          sender: currentConversation?.participants.find(p => p !== address) || '',
          recipient: address,
          content: 'Hey! Love your latest content on HDH!',
          timestamp: Date.now() - 3600000,
          encrypted: true
        },
        {
          id: 'msg-2',
          sender: address,
          recipient: currentConversation?.participants.find(p => p !== address) || '',
          content: 'Thank you! I\'m glad you enjoyed it. More coming soon!',
          timestamp: Date.now() - 1800000,
          encrypted: true
        },
        {
          id: 'msg-3',
          sender: currentConversation?.participants.find(p => p !== address) || '',
          recipient: address,
          content: 'Can\'t wait! Do you have any exclusive content for subscribers?',
          timestamp: Date.now() - 900000,
          encrypted: true
        }
      ]

      setMessages(mockMessages)
    } catch (err) {
      setError('Failed to load messages')
      console.error('Error loading messages:', err)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (conversationId: string): Promise<void> => {
    setConversations(prev => prev.map(conv => 
      conv.id === conversationId 
        ? { ...conv, unreadCount: 0 }
        : conv
    ))
  }

  const value = {
    conversations,
    currentConversation,
    messages,
    loading,
    error,
    createConversation,
    sendMessage,
    loadMessages,
    setCurrentConversation,
    markAsRead
  }

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  )
}

export function useMessaging() {
  const context = useContext(MessagingContext)
  if (context === undefined) {
    throw new Error('useMessaging must be used within a MessagingProvider')
  }
  return context
}
